import { SystemUserRoles } from '@engagement-web-app/common';

/**
 * Single source of truth for role-gated actions on the system (internal) side.
 *
 * Shape: `{ [resource]: { [action]: allowedRoles[] } }`. Only actions that are
 * actually restricted need to be listed — anything not defined here is not gated.
 * Keep this aligned with `doc/permission-system.md`.
 */
const SystemUserPermissionDef = {
    systemUsers: {
        create: [SystemUserRoles.enum.ADMIN],
        update: [SystemUserRoles.enum.ADMIN],
        delete: [SystemUserRoles.enum.ADMIN],
        passwordReset: [SystemUserRoles.enum.ADMIN],
        mfaReset: [SystemUserRoles.enum.ADMIN],
    },
    organization: {
        mfaToggle: [SystemUserRoles.enum.ADMIN],
    },
} as const;

type SystemPermissionResource = keyof typeof SystemUserPermissionDef;

type SystemPermissionAction<R extends SystemPermissionResource> = keyof (typeof SystemUserPermissionDef)[R];

/**
 * Signature of the `can(resource, action)` check exposed by the permission context.
 * This is supposed to be used from SystemPermissionProvider and not used individually.
 * */
export type SystemPermissionCan = <R extends SystemPermissionResource>(
    resource: R,
    action: SystemPermissionAction<R>
) => boolean;

/**
 * Returns whether the given role is allowed to perform `resource.action`.
 * A missing role (unauthenticated) is never allowed.
 */
export function hasSystemPermission<R extends SystemPermissionResource>(
    roleId: string | undefined,
    resource: R,
    action: SystemPermissionAction<R>
): boolean {
    if (!roleId) return false;
    const actions = SystemUserPermissionDef[resource] as Record<string, readonly string[]>;
    return actions[action as string].includes(roleId);
}
